import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { type Accept, ErrorCode, useDropzone } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { FileNumberDisclaimer } from './file-number-disclaimer';
import { FilesTable } from './files-table';
import { ImportSettingsDialog } from './import-settings-dialog';
import { UploadBreakdownModal } from './upload-breakdown-modal';
import { UploadStatusList } from './upload-status-indicator';
import { FileUploadInput } from './uploader';
import { useFileUploadLogic } from './useFileUploadLogic';
import { useUploadGuards } from './useUploadGuards';
import { useUppyUpload } from './useUppyUpload';
import { formatFileSize } from './utils';
import {
  MAX_UPLOAD_FILE_COUNT,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_PST_SIZE_BYTES,
  MAX_UPLOAD_ZIP_SIZE_BYTES,
} from '../../../../../backend/src/constants/upload-limits';
import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { TERMINAL_UPLOAD_STATUSES, UploadFailureReason } from '../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { PendingFailure, useSendPendingFailures } from '@/api/mutations/useSendPendingFailures';
import { useWarmDocumentConverter } from '@/api/mutations/useWarmDocumentConverter';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { QueryEntity } from '@/api/types';
import type { UploadHistory } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { AudioReviewSummary } from '@/new/components/audio-speaker-tagging/audio-review-summary';
import { FilePreviewDrawer } from '@/new/components/drawers/file-preview-drawer';
import { Icon } from '@/new/components/icon';
import { ProjectedCreditLimitCard } from '@/new/components/projected-credit-limit-card';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';
import { UploadDetailModal } from '@/new/pages/matters/_matterId/settings/file-history/components/modals/upload-detail-modal';

export function FileUploader({
  uploadHistory,
  setFilesProcessing,
  setFilesExist,
  isMatterCreator = false,
  creditLimitMessage = null,
}: {
  uploadHistory: UploadHistory[] | undefined;
  setFilesProcessing?: (processing: boolean) => void;
  setFilesExist?: (v: boolean) => void;
  isMatterCreator?: boolean;
  /** Why the staged files can't be processed, shown above the upload settings. */
  creditLimitMessage?: string | null;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;
  const { user } = useUserContext();

  const { mutate: warmDocumentConverter } = useWarmDocumentConverter(matterId);
  const hasRequestedConverterWarmup = useRef(false);

  useEffect(() => {
    if (hasRequestedConverterWarmup.current) return;
    hasRequestedConverterWarmup.current = true;
    warmDocumentConverter();
  }, [warmDocumentConverter]);

  // Fetch matter data to get allDocumentsCaseId
  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? null;

  // Use a ref to avoid recreating callbacks when allDocumentsCaseId changes
  const allDocumentsCaseIdRef = useRef<string | null>(null);
  allDocumentsCaseIdRef.current = allDocumentsCaseId;

  // Whether to unpack email attachments (.msg/.eml) into separate documents.
  const [unpackEmail, setUnpackEmail] = useState(true);
  const unpackEmailRef = useRef(true);
  unpackEmailRef.current = unpackEmail;
  const [deduplicateDocuments, setDeduplicateDocuments] = useState(true);
  const deduplicateDocumentsRef = useRef(true);
  deduplicateDocumentsRef.current = deduplicateDocuments;
  // Relativity-only: fall back to OCR'd text when the native file format is
  // not supported by Wexler. Opt-in per import.
  const [fallbackToExtractedText, setFallbackToExtractedText] = useState(false);
  const fallbackToExtractedTextRef = useRef(false);
  fallbackToExtractedTextRef.current = fallbackToExtractedText;

  const queryClient = useQueryClient();

  // TODO: Adjust this based on how many files are currently in the matter
  const maxFiles = MAX_UPLOAD_FILE_COUNT;

  const authInfo = useAuthInfo();
  const permittedFileTypes = allowedFileTypes;

  const acceptedFileTypes = useMemo<Accept>(
    () => Object.fromEntries(permittedFileTypes.map((ext) => [ext, [] as string[]])),
    [permittedFileTypes],
  );

  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [importSettingsOpen, setImportSettingsOpen] = useState(false);
  const [uploadDetailUploadId, setUploadDetailUploadId] = useState<string | null>(null);
  const [activeUploadStatusIds, setActiveUploadStatusIds] = useState<string[]>([]);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const { mutateAsync: sendPendingFailuresMutation } = useSendPendingFailures({ matterId });
  const { data: files, refetch: refetchFiles, isLoading: isLoadingFiles } = useGetFiles(matterId);

  // --- Uppy upload hook ---
  const {
    onUploadUppy,
    isUploading,
    setIsUploading,
    hasStartedUpload,
    setHasStartedUpload,
    uploadSessions,
    uppyProgressByUpload,
    handleProcessingComplete,
    uploadSessionsRef,
  } = useUppyUpload({
    matterId,
    allDocumentsCaseIdRef,
    unpackEmailRef,
    deduplicateDocumentsRef,
    sendPendingFailures: sendPendingFailuresMutation,
    refetchFiles,
    queryClient,
    maxFiles,
    onShowUploadDetails: setUploadDetailUploadId,
  });

  // --- Upload guards (beforeunload + navigation blocking) ---
  const { pendingNavigation, setPendingNavigation, originalNavigatorRef } = useUploadGuards({
    isUploading,
    uploadSessionsRef,
    matterId,
  });

  const { handleFileAccepted, isRelativityModalOpen, setIsRelativityModalOpen, connectRelativity } = useFileUploadLogic(
    {
      maxFiles,
    },
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    noClick: true,
    accept: acceptedFileTypes,
    useFsAccessApi: false,
    // Use a single onDrop (not separate onDropAccepted/onDropRejected) so the
    // accepted and rejected files from one drop can share a single uploadId,
    // producing one upload-history row instead of two.
    onDrop: (acceptedFiles, rejections) => {
      // Dedup by error code + message so dropping N oversized files shows one toast
      // per distinct message (e.g. file vs ZIP vs PST limits), not N.
      const seenErrors = new Set<string>();
      for (const rejection of rejections) {
        for (const error of rejection.errors) {
          const errorKey = `${error.code}:${error.message}`;
          if (seenErrors.has(errorKey)) continue;
          seenErrors.add(errorKey);
          if (error.code === ErrorCode.FileTooLarge) {
            // The validator's message states the applicable limit for this file kind.
            toast.error(`${error.message} Please contact support@wexler.ai if you need large file support.`);
          } else if (error.code === ErrorCode.TooManyFiles) {
            toast.error(
              `There are too many files in the upload. The maximum is ${maxFiles.toLocaleString()} files. Please contact support@wexler.ai if you need assistance in uploading many files.`,
            );
          } else if (error.code === ErrorCode.FileInvalidType) {
            toast.error(
              `Some files were skipped because their format is not supported. Supported formats include PDF, Word, Excel, PowerPoint, images, emails, PST archives, audio and video files and ZIP archives.`,
            );
          } else {
            toast.error(error.message);
          }
        }
      }

      // Files rejected by Dropzone never reach Uppy, so Uppy's restriction-failed
      // recording never fires for them. Record them the same way useUppyUpload
      // does, so files rejected on the frontend show up in the upload breakdown
      // rather than being silently dropped.
      const caseId = allDocumentsCaseIdRef.current;
      if (!caseId) {
        if (acceptedFiles.length > 0) handleFileAccepted(acceptedFiles, onUploadUppy);
        return;
      }

      const failures: PendingFailure[] = rejections
        .map((rejection): PendingFailure | null => {
          // A rejection can carry multiple errors; map to the reason we record.
          const rejectedReason = rejection.errors.some((error) => error.code === ErrorCode.FileTooLarge)
            ? UploadFailureReason.FileTooLarge
            : rejection.errors.some((error) => error.code === ErrorCode.FileInvalidType)
              ? UploadFailureReason.UnsupportedFileFormat
              : null;
          if (!rejectedReason) return null;
          return {
            filename: rejection.file.name,
            extension: rejection.file.name.split('.').pop() ?? '',
            rejectedReason,
          };
        })
        .filter((failure): failure is PendingFailure => failure !== null);

      // One uploadId for the whole drop.
      const uploadId = uuidv4();

      if (acceptedFiles.length > 0) {
        // Hand the rejected files to the Uppy hook so they're recorded under the
        // same uploadId as the accepted files. Uppy owns /done (it finalizes once
        // every accepted file settles), so we must NOT signal done here.
        handleFileAccepted(acceptedFiles, (files) => onUploadUppy(files, { uploadId, droppedFailures: failures }));
      } else if (failures.length > 0) {
        // All files were rejected — nothing reaches Uppy, so record the failures
        // and signal done ourselves. Without /done the findOrCreate'd
        // upload-history row never finalizes and lingers as 'processing'; there's
        // no real upload in flight, so finalize immediately.
        void (async () => {
          try {
            await sendPendingFailuresMutation({ caseId, uploadId, failures });
          } finally {
            try {
              const token = await authInfo.tokens.getAccessToken();
              await fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${uploadId}/done`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                keepalive: true,
              });
            } catch {
              // Best effort: this should not block invalidation or surface as unhandled.
            }
            queryClient.invalidateQueries([QueryEntity.UploadHistory]);
          }
        })();
      }
    },
    // Enforce the per-file size limit; the applicable limits live in backend/src/constants/upload-limits.ts.
    // ZIP and PST archives are unpacked server-side, so they get their own (larger) limits.
    validator: (file) => {
      const name = file.name?.toLowerCase() ?? '';
      const isZip = name.endsWith('.zip');
      const isPst = name.endsWith('.pst');
      const limit = isZip ? MAX_UPLOAD_ZIP_SIZE_BYTES : isPst ? MAX_UPLOAD_PST_SIZE_BYTES : MAX_UPLOAD_FILE_SIZE_BYTES;
      if (file.size > limit) {
        const kind = isZip ? 'ZIP archive' : isPst ? 'PST archive' : 'File';
        return {
          code: ErrorCode.FileTooLarge,
          message: `${kind} is too large. The maximum size is ${formatFileSize(limit)}.`,
        };
      }
      return null;
    },
    maxFiles,
  });

  // Uploads currently being processed by the backend
  const processingUploads = useMemo(
    () => (uploadHistory ?? []).filter((u) => u.status === 'processing'),
    [uploadHistory],
  );

  // A local Uppy session has been added but isn't yet reflected as a non-terminal
  // row in upload_history. Carries the local-only signal the parent can't derive;
  // the parent handles the shared-cache "any upload processing" check on its own.
  const hasUnresolvedSession = useMemo(
    () =>
      uploadSessions.some((s) => {
        const h = uploadHistory?.find((u) => u.id === s.uploadId);
        return !h || !h.status || !TERMINAL_UPLOAD_STATUSES.has(h.status);
      }),
    [uploadSessions, uploadHistory],
  );

  // Used to pass up to parent state the fact that an upload is ongoing
  useEffect(() => {
    setFilesProcessing?.(isUploading || hasUnresolvedSession);
  }, [isUploading, hasUnresolvedSession]);

  useEffect(() => {
    if (files) {
      setFilesExist?.(files.length > 0);
    }
  }, [files, setFilesExist]);

  // Expand files section when there are processing uploads discovered from DB
  useEffect(() => {
    if (processingUploads.length > 0) {
      setHasStartedUpload(true);
    }
  }, [processingUploads]);

  // Expand files section once any upload starts or files already exist
  const hasFiles = hasStartedUpload || (files !== undefined && files.length > 0);
  const filesAwaitingProcessingCount = files?.length ?? 0;
  // Pages are counted at upload, so this is the page volume about to be submitted.
  // Files still converting have no count yet and contribute nothing.
  const pagesAwaitingProcessingCount = (files ?? []).reduce((total, file) => total + (file.numberOfPages ?? 0), 0);
  const uploadProcessingLabel = [
    processingUploads.length > 0
      ? `${processingUploads.length} upload${processingUploads.length !== 1 ? 's' : ''} processing`
      : null,
    filesAwaitingProcessingCount > 0
      ? `${filesAwaitingProcessingCount.toLocaleString()} file${filesAwaitingProcessingCount !== 1 ? 's' : ''}`
      : null,
    pagesAwaitingProcessingCount > 0
      ? `${pagesAwaitingProcessingCount.toLocaleString()} page${pagesAwaitingProcessingCount !== 1 ? 's' : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const visibleUploadIds = useMemo(
    () => new Set((files ?? []).map((file) => file.uploadId).filter((uploadId): uploadId is string => !!uploadId)),
    [files],
  );

  const latestUploadResult = useMemo(() => {
    if (!user?.id) return undefined;

    return (uploadHistory ?? [])
      .filter((upload) => {
        const createdAt = new Date(upload.createdAt).getTime();
        return (
          upload.createdById === user.id &&
          TERMINAL_UPLOAD_STATUSES.has(upload.status) &&
          Number.isFinite(createdAt) &&
          visibleUploadIds.has(upload.id)
        );
      })
      .sort((a, b) => {
        const byCreatedAt = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return byCreatedAt || b.id.localeCompare(a.id);
      })
      .at(0);
  }, [uploadHistory, user?.id, visibleUploadIds]);

  const handleActiveUploadStatusIdsChange = useCallback((uploadIds: string[]) => {
    setActiveUploadStatusIds((currentUploadIds) =>
      currentUploadIds.join('\0') === uploadIds.join('\0') ? currentUploadIds : uploadIds,
    );
  }, []);

  const shouldShowUploadResultCoachMark = !!latestUploadResult && activeUploadStatusIds.length === 0;

  return (
    <div className="relative flex h-full overflow-y-hidden focus-visible:outline-none" {...getRootProps()}>
      {/* Dashed border */}
      <div
        className={cn(
          isDragActive ? 'opacity-100' : 'opacity-0',
          'pointer-events-none absolute inset-0 z-20 bg-black/6 duration-150',
        )}
      />
      {/* Upload Section */}
      <div
        className={cn(
          hasFiles ? 'w-112' : 'w-1/2',
          isDragActive ? 'pointer-events-none bg-gray-200/60' : 'bg-gray-100',
          'relative flex items-center justify-center border-r px-6 duration-150',
        )}
      >
        <FileUploadInput
          matterId={matterId}
          subsetId={allDocumentsCaseId}
          unpackEmail={unpackEmail}
          deduplicateDocuments={deduplicateDocuments}
          fallbackToExtractedText={fallbackToExtractedText}
          onOpenImportSettings={() => setImportSettingsOpen(true)}
          permittedFileTypes={permittedFileTypes}
          // Dropzone props
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          open={open}
          // useFileUploadLogic props
          isRelativityModalOpen={isRelativityModalOpen}
          setIsRelativityModalOpen={setIsRelativityModalOpen}
          connectRelativity={connectRelativity}
        />

        <FileNumberDisclaimer />
      </div>

      {/* Files Section */}
      <div className={cn('flex min-w-sm grow flex-col px-6 py-4 pr-4 pb-1', isDragActive && 'pointer-events-none')}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-serif text-2xl font-medium">Files</h2>
            <Tooltip>
              <TooltipTrigger className="-mb-0.5 pl-1 text-gray-400">
                <Icon className="lucide--info" />
              </TooltipTrigger>
              <TooltipContent side="right">
                These files have been uploaded, but not yet processed by Wexler.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            {uploadProcessingLabel && <p className="mr-1 text-xs text-gray-500">{uploadProcessingLabel}</p>}

            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (isMatterCreator) {
                  setFlowModalOpen(true);
                  trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownModal, { matterId });
                } else {
                  trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownLink, { matterId });
                  window.open(`/matters/${matterId}/settings/file-history`, '_blank');
                }
              }}
            >
              <Icon className="lucide--git-branch" />
              View Upload Breakdown
            </Button>

            <ImportSettingsDialog
              open={importSettingsOpen}
              onOpenChange={setImportSettingsOpen}
              unpackEmail={unpackEmail}
              onUnpackEmailChange={setUnpackEmail}
              deduplicateDocuments={deduplicateDocuments}
              onDeduplicateDocumentsChange={setDeduplicateDocuments}
              fallbackToExtractedText={fallbackToExtractedText}
              onFallbackToExtractedTextChange={setFallbackToExtractedText}
            />
          </div>
        </div>

        <ProjectedCreditLimitCard message={creditLimitMessage} className="my-1.5" />

        {(!unpackEmail || !deduplicateDocuments || fallbackToExtractedText) && (
          <div className="my-1.5 space-y-1.75 rounded-lg border bg-gray-50 p-2 px-3">
            {!unpackEmail && (
              <div className="flex gap-2">
                <div>
                  <Icon className="lucide--info mt-0.75 text-gray-400" />
                </div>
                <div>
                  <p className="text-gray-600">
                    Email unpacking is disabled.{' '}
                    <button
                      className="inline-flex items-center gap-1 text-blue-500"
                      onClick={() => setImportSettingsOpen(true)}
                    >
                      Open settings <Icon className="lucide--arrow-up-right inline-block" />
                    </button>
                  </p>
                </div>
              </div>
            )}

            {!deduplicateDocuments && (
              <div className="flex gap-2">
                <div>
                  <Icon className="lucide--info mt-0.75 text-gray-400" />
                </div>
                <div>
                  <p className="text-gray-600">
                    Deduplication is disabled. Ensure all uploaded documents are to be submitted for processing
                    individually.{' '}
                    <button
                      className="inline-flex items-center gap-1 text-blue-500"
                      onClick={() => setImportSettingsOpen(true)}
                    >
                      Open settings <Icon className="lucide--arrow-up-right inline-block" />
                    </button>
                  </p>
                </div>
              </div>
            )}

            {fallbackToExtractedText && (
              <div className="flex gap-2">
                <div>
                  <Icon className="lucide--info mt-0.75 text-gray-400" />
                </div>
                <div>
                  <p className="text-gray-600">
                    Fallback to extracted text is enabled. Files with formats Wexler doesn't natively support will fall
                    back to using the OCR'd text from Relativity.{' '}
                    <button
                      className="inline-flex items-center gap-1 text-blue-500"
                      onClick={() => setImportSettingsOpen(true)}
                    >
                      Open settings <Icon className="lucide--arrow-up-right inline-block" />
                    </button>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <UploadStatusList
          uploadSessions={uploadSessions}
          uploadHistory={uploadHistory}
          uppyProgressByUpload={uppyProgressByUpload}
          matterId={matterId}
          onProcessingComplete={handleProcessingComplete}
          onActiveUploadStatusIdsChange={handleActiveUploadStatusIdsChange}
        />

        {!isMatterCreator && <AudioReviewSummary matterId={matterId} className="mx-0 my-2" />}

        {hasFiles ? (
          <FilesTable
            files={files ?? []}
            isLoading={isLoadingFiles}
            onViewUploadDetails={setUploadDetailUploadId}
            uploadResultCoachMarkUploadId={shouldShowUploadResultCoachMark ? latestUploadResult.id : undefined}
            onPreviewDoc={setPreviewDocId}
          />
        ) : isLoadingFiles ? (
          <Skeleton className="mt-2 h-4 w-16" />
        ) : (
          <p className="mt-2 text-gray-500">No files added yet</p>
        )}
      </div>

      <UploadBreakdownModal open={flowModalOpen} onOpenChange={setFlowModalOpen} matterId={matterId} />

      {uploadDetailUploadId && (
        <UploadDetailModal
          open={!!uploadDetailUploadId}
          onOpenChange={(open) => !open && setUploadDetailUploadId(null)}
          matterId={matterId}
          uploadId={uploadDetailUploadId}
        />
      )}

      {/* Confirmation dialog when navigating away during upload */}
      <Dialog
        open={pendingNavigation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingNavigation(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload in progress</DialogTitle>
            <DialogDescription>
              Files are still being uploaded to storage. If you leave now, only files that have already been uploaded
              will be processed.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Once uploading finishes, processing (deduplication, extraction, etc.) will continue in the background even
            if you navigate away.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="default" onClick={() => setPendingNavigation(null)}>
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                // Signal done for all sessions — already-submitted batches continue
                // processing and remain visible as "processing" in upload history.
                void (async () => {
                  const token = await authInfo.tokens.getAccessToken();
                  for (const session of uploadSessionsRef.current) {
                    await fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${session.uploadId}/done`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    }).catch(() => {});
                  }
                  setIsUploading(false);

                  if (pendingNavigation && originalNavigatorRef.current) {
                    const { action, args } = pendingNavigation;
                    const orig = originalNavigatorRef.current;
                    setPendingNavigation(null);
                    if (action === 'push') {
                      (orig.push as Function)(...args);
                    } else {
                      (orig.replace as Function)(...args);
                    }
                  }
                })();
              }}
            >
              Leave
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <FilePreviewDrawer docId={previewDocId} onClose={() => setPreviewDocId(null)} />
    </div>
  );
}
